// Copyright (c) 2018, Frappe Technologies Pvt. Ltd. and Contributors
// MIT License. See license.txt

import "./calendar_print";
import { EventPopupManager } from "./event_popup.js";

frappe.provide("frappe.views.calendar");

const day_map = {
	Sunday: 0,
	Monday: 1,
	Tuesday: 2,
	Wednesday: 3,
	Thursday: 4,
	Friday: 5,
	Saturday: 6,
};

frappe.views.CalendarView = class CalendarView extends frappe.views.ListView {
	static load_last_view() {
		const route = frappe.get_route();
		if (route.length === 3) {
			const doctype = route[1];
			const user_settings = frappe.get_user_settings(doctype)["Calendar"] || {};
			route.push(user_settings.last_calendar || "default");
			frappe.route_flags.replace_route = true;
			frappe.set_route(route);
			return true;
		} else {
			return false;
		}
	}

	toggle_result_area() {}

	get view_name() {
		// __("Calendar")
		return "Calendar";
	}

	setup_page() {
		this.hide_page_form = false;
		super.setup_page();
	}

	async setup_defaults() {
		await super.setup_defaults();
		this.page_title = __("{0} Calendar", [this.page_title]);
		this.calendar_settings = frappe.views.calendar[this.doctype] || {};
		this.calendar_name = frappe.get_route()[3];
	}

	before_refresh() {
		super.before_refresh();
		if (
			this.calendar_settings.filters &&
			this.calendar_settings.filters.length > 0 &&
			Array.isArray(this.calendar_settings.filters[0])
		) {
			this.filter_area.add(this.calendar_settings.filters);
		}
	}

	setup_view() {
		this.sort_selector.wrapper.hide();
		this.settings.onload && this.settings.onload(this);
	}

	get_view_settings() {
		return {};
	}

	before_render() {
		super.before_render();
		this.save_view_user_settings({
			last_calendar: this.calendar_name,
		});
	}

	render() {
		if (this.calendar) {
			this.calendar.refresh();
			return;
		}

		this.$result.css("overflow", "visible");

		this.load_lib
			.then(() => this.get_calendar_preferences())
			.then((options) => {
				this.calendar = new frappe.views.Calendar(options);
			});
	}

	render_list() {
		// noop
	}

	get_calendar_preferences() {
		const options = {
			doctype: this.doctype,
			parent: this.$result,
			page: this.page,
			list_view: this,
		};
		const calendar_name = this.calendar_name;

		return new Promise((resolve) => {
			if (calendar_name.toLowerCase() === "default") {
				Object.assign(options, frappe.views.calendar[this.doctype]);
				resolve(options);
			} else {
				frappe.model.with_doc("Calendar View", calendar_name, () => {
					const doc = frappe.get_doc("Calendar View", calendar_name);
					if (!doc) {
						frappe.show_alert(
							__("{0} is not a valid Calendar. Redirecting to default Calendar.", [
								calendar_name.bold(),
							])
						);
						frappe.set_route("List", this.doctype, "Calendar", "default");
						return;
					}
					Object.assign(options, {
						field_map: {
							id: "name",
							start: doc.start_date_field,
							end: doc.end_date_field,
							title: doc.subject_field,
							allDay: doc.all_day_field,
							status: doc.status_field,
							color: doc.color_field,
							rrule: doc.recurrence_rule_field,
							secondary_status: doc.secondary_status_field,
						},
						secondary_status_color: doc.secondary_status.reduce(
							(obj, f) => Object.assign(obj, { [f.value]: f.color }),
							{}
						),
						calendar_defaults: {
							slots_start_time: doc.daily_minimum_time,
							slots_end_time: doc.daily_maximum_time,
							first_day: doc.first_day ? day_map[doc.first_day] : null,
							display_event_time: doc.display_event_time == 1 ? true : false,
							display_event_end: doc.display_event_end == 1 ? true : false,
						},
					});

					resolve(options);
				});
			}
		});
	}

	get required_libs() {
		return "calendar.bundle.js";
	}
};

frappe.views.Calendar = class frappeCalendar {
	/** @type {frappe.views.CalendarView} */
	list_view = null;

	constructor(options) {
		$.extend(this, options);
		this.fullCalendar = null;
		this.calendar_defaults =
			this.calendar_defaults && Object.keys(this.calendar_defaults)
				? this.calendar_defaults
				: {
						display_event_time: true,
						display_event_end: true,
				  };
		this.add_header();

		this.field_map = this.field_map || {
			id: "name",
			start: "start",
			end: "end",
			allDay: "all_day",
			convertToUserTz: "convert_to_user_tz",
		};
		this.color_map = {
			danger: "red",
			success: "green",
			warning: "orange",
			default: "blue",
		};
		this.get_default_options();
	}

	get_default_options() {
		return new Promise((resolve) => {
			let initialView = localStorage.getItem("cal_initialView");
			let weekends = localStorage.getItem("cal_weekends");
			let defaults = {
				initialView:
					initialView &&
					["timeGridDay", "timeGridWeek", "dayGridMonth"].includes(initialView)
						? initialView
						: "dayGridMonth",
				weekends: weekends ? weekends : true,
			};
			resolve(defaults);
		}).then((defaults) => {
			Object.assign(this.calendar_defaults, defaults);
			this.make_page();
			this.setup_options(this.calendar_defaults);
			this.make();
			this.setup_view_mode_button(this.calendar_defaults);
			this.bind();
			this.get_preview_fields();
		});
	}

	make_page() {
		const me = this;

		// add links to other calendars
		me.page.clear_user_actions();

		me.page.add_menu_item(
			__("Print"),
			() => {
				const style = document.createElement("style");
				style.append("@page { size: A4 landscape; }");
				document.head.appendChild(style);
				window.print();
				style.remove();
			},
			false,
			"Ctrl+P"
		);

		$.each(frappe.boot.calendars, function (i, doctype) {
			if (frappe.model.can_read(doctype)) {
				me.page.add_inner_button(
					__(doctype),
					function () {
						frappe.set_route("List", doctype, me.list_view.view_name);
					},
					__("Go To")
				);
			}
		});

		$(this.parent).on("show", function () {
			// (this used the FullCalendar v3 jQuery API — me.$cal.fullCalendar
			// is undefined on v6, so it threw on every page revisit)
			me.fullCalendar.refetchEvents();
			me.set_calendar_height();
		});
	}

	make() {
		this.$wrapper = this.parent;
		this.make_toolbar();
		// no horizontal padding of its own — the view container's rail is the
		// alignment edge for the toolbar, the grid and the footnote alike
		this.$cal = $("<div id='fc-calendar-wrapper' class='pt-3'>").appendTo(this.$wrapper);
		this.footnote_area = frappe.utils.set_footnote(
			this.footnote_area,
			this.$wrapper,
			__("Select or drag across time slots to create a new event.")
		);
		this.footnote_area.addClass("pb-4").css({
			"border-top": "0px",
		});
		this.fullCalendar = new frappe.FullCalendar(this.$cal[0], this.cal_options);
		this.fullCalendar.render();

		if (this.secondary_status_color) {
			this.show_secondary_status_legend(); // @dokos
		}

		this.set_calendar_height();
		$(window).on(
			"resize.fc-calendar",
			frappe.utils.debounce(() => this.set_calendar_height(), 300)
		);
	}

	// size the calendar to the space left in the scroll column, so the view
	// fits the viewport instead of growing past it (FullCalendar's default
	// aspect-ratio sizing overflows on tall months)
	set_calendar_height() {
		if (!this.$cal.is(":visible")) return;
		const main = document.querySelector(".main-section");
		if (!main) return;
		const top = this.$cal[0].getBoundingClientRect().top - main.getBoundingClientRect().top;
		const footnote_height = this.footnote_area ? this.footnote_area.outerHeight(true) : 0;
		const available = main.clientHeight - top - footnote_height;
		this.fullCalendar.setOption("height", Math.max(available, 400));
	}

	// [prev] [title ⌄ → jump-to-date picker] [next] ..... [Today] [Month|Week|Day]
	// Month/Week/Day is a radio group (you are always in exactly one);
	// Today is an action, so it's a plain button
	make_toolbar() {
		// the label is filled by the datesSet callback on first render
		this.$toolbar_title = frappe.ui.button({
			label: __("Calendar"),
			variant: "ghost",
			css_class: "text-lg-medium text-ink-gray-7",
		});
		this.date_popover = new frappe.ui.Popover({
			trigger: this.$toolbar_title,
			content: () => this.make_date_jumper(),
			css_class: "calendar-date-jumper",
		});

		this.view_button_group = new frappe.ui.TabButtons({
			label: __("Calendar View"),
			options: [
				{ label: __("Month"), value: "dayGridMonth" },
				{ label: __("Week"), value: "timeGridWeek" },
				{ label: __("Day"), value: "timeGridDay" },
			],
			value: this.cal_options.initialView,
			on_change: (value) => {
				this.fullCalendar.changeView(value);
				this.set_localStorage_option("cal_initialView", value);
			},
		});

		this.$toolbar = $('<div class="flex items-center gap-2 pt-4"></div>').append(
			frappe.ui.button({
				icon: "chevron-left",
				variant: "ghost",
				title: __("Previous"),
				onclick: () => this.fullCalendar.prev(),
			}),
			this.$toolbar_title,
			frappe.ui.button({
				icon: "chevron-right",
				variant: "ghost",
				title: __("Next"),
				onclick: () => this.fullCalendar.next(),
			}),
			$('<div class="grow"></div>'),
			(this.today_button = frappe.ui.button({
				label: __("Today"),
				onclick: () => this.fullCalendar.today(),
			})),
			this.view_button_group.$el
		);
		this.$wrapper.append(this.$toolbar);
	}

	// inline air-datepicker (the desk-standard picker) inside the title
	// popover — picking any date jumps the calendar there in the current view
	make_date_jumper() {
		const wrapper = document.createElement("div");
		let lang = (frappe.boot.user && frappe.boot.user.language) || "en";
		if (!$.fn.datepicker.language[lang]) lang = "en";

		// match the picker to the view: month view jumps by month,
		// week/day views by day
		const month_only = this.fullCalendar.view.type === "dayGridMonth";

		let ready = false;
		$(wrapper).datepicker({
			language: lang,
			firstDay: frappe.datetime.get_first_day_of_the_week_index(),
			...(month_only ? { view: "months", minView: "months" } : {}),
			onSelect: (fd, date) => {
				if (!ready || !date) return;
				this.fullCalendar.gotoDate(date);
				this.date_popover.close();
			},
		});
		// preselect where the calendar currently is; selectDate fires
		// onSelect synchronously, so the flag keeps it from immediately
		// re-navigating and closing the popover
		$(wrapper).data("datepicker").selectDate(this.fullCalendar.getDate());
		ready = true;
		return wrapper;
	}

	setup_view_mode_button(defaults) {
		$(this.footnote_area).find(".btn-weekend").detach();
		this.footnote_area.append(
			frappe.ui.button({
				label: defaults.weekends ? __("Hide Weekends") : __("Show Weekends"),
				size: "xs",
				css_class: "btn-weekend",
			})
		);
	}

	set_localStorage_option(option, value) {
		localStorage.removeItem(option);
		localStorage.setItem(option, value);
	}

	bind() {
		const me = this;
		me.$wrapper.on("click", ".btn-weekend", function () {
			me.cal_options.weekends = !me.cal_options.weekends;
			me.fullCalendar.setOption("weekends", me.cal_options.weekends);
			me.set_localStorage_option("cal_weekends", me.cal_options.weekends);
			me.setup_view_mode_button(me.cal_options);
		});
	}

	// @dokos: dokos renders a secondary-status legend above the calendar grid
	// (Calendar View.secondary_status_field). Upstream has no such header, so
	// a port that takes upstream's calendar.js verbatim drops both methods and
	// leaves the constructor's add_header() call dangling.
	add_header() {
		// @dokos
		this.custom_header = document.createElement("div");
		this.custom_header.className = "calendar-view-header flex flex-between p-3";

		this.legendContainer = document.createElement("div");
		this.legendContainer.className = "pm-legend-container flex align-center";
		this.custom_header.appendChild(this.legendContainer);

		this.list_view.$result[0].parentNode.insertBefore(
			this.custom_header,
			this.list_view.$result[0]
		);
	}

	show_secondary_status_legend() {
		// @dokos
		if (!this.legendContainer || !this.field_map.secondary_status) return;

		frappe.model.with_doctype(this.doctype, () => {
			const meta = frappe.get_meta(this.doctype);
			const statusField = meta.fields.find(
				(f) => f.fieldname === this.field_map.secondary_status
			);

			if (!statusField) return;

			// Clear existing content to prevent duplicates on refresh
			this.legendContainer.innerHTML = "";

			// Create Label Section
			const label = document.createElement("span");
			label.className = "legend-label text-muted mr-3 text-small font-weight-bold";
			label.innerText = `${__(statusField.label)}:`;
			this.legendContainer.appendChild(label);

			// Create List Wrapper
			const statusList = document.createElement("div");
			statusList.className = "flex align-center";
			statusList.style.gap = "2px";

			// Generate Legend Items
			Object.keys(this.secondary_status_color).forEach((status) => {
				const colorClass = this.secondary_status_color[status];
				const item = document.createElement("div");
				item.className = "legend-item flex align-center";

				// Note: Use standard Dokos/Frappe indicator classes for consistency
				item.innerHTML = `
					<span class="indicator-pill ${colorClass} text-xs">
						${__(status)}
					</span>
				`;
				statusList.appendChild(item);
			});

			this.legendContainer.appendChild(statusList);
		});
	}

	get_system_datetime(date) {
		date._offset = moment(date).tz(frappe.sys_defaults.time_zone)._offset;
		return frappe.datetime.convert_to_system_tz(moment(date).locale("en"));
	}

	setup_options(defaults) {
		const me = this;

		this.cal_options = {
			locale: frappe.get_cookie("preferred_language") || frappe.boot.lang || "en",
			plugins: frappe.FullCalendar.LimitedPlugins,
			eventDisplay: "block",
			// the toolbar is ours (make_toolbar), not FullCalendar's — no
			// more stripping fc-button classes and re-adding them after every
			// re-render
			headerToolbar: false,
			datesSet: (info) => {
				// the title is an es-button now — swap only its label text
				this.$toolbar_title &&
					this.$toolbar_title.find(".es-button__label").text(info.view.title);
				if (this.today_button) {
					// no-op when the visible range already contains today
					// (activeEnd is exclusive)
					const now = new Date();
					this.today_button.prop(
						"disabled",
						info.view.activeStart <= now && now < info.view.activeEnd
					);
				}
			},
			height: "calc(100svh - 220px)",
			editable: true,
			selectable: true,
			selectMirror: true,
			forceEventDuration: true,
			displayEventTime: defaults.display_event_time,
			displayEventEnd: defaults.display_event_end,
			initialView: defaults.initialView,
			weekends: defaults.weekends,
			nowIndicator: true,
			snapDuration: "00:15:00",
			slotDuration: "00:30:00",
			events: function (parameters, callback) {
				return frappe
					.xcall(
						me.get_events_method || "frappe.desk.calendar.get_events",
						me.get_args(parameters.start, parameters.end)
					)
					.then((r) => {
						let events = r || [];
						events = me.prepare_events(events);
						if (!defaults.slots_start_time && !defaults.slots_end_time) {
							me.set_min_max_times(events, {
								min: "10:00:00",
								max: "16:00:00",
								padHours: 1,
							});
						}
						callback(events);
					});
			},
			eventClick: function (info) {
				// edit event description or delete
				const doctype =
					info.event.doctype || info.event.extendedProps.doctype || me.doctype;
				if (frappe.model.can_read(doctype)) {
					frappe.set_route("Form", doctype, info.event.id);
				}
			},
			eventDrop: function (info) {
				me.update_event(info);
				$(info.el).tooltip("dispose");
			},
			eventResize: function (info) {
				me.update_event(info);
				$(info.el).tooltip("dispose");
			},
			select: function (selectionInfo) {
				if (
					selectionInfo.view.type === "dayGridMonth" &&
					selectionInfo.end - selectionInfo.start === 86400000
				) {
					// detect single day click in month view
					return;
				}
				const event = frappe.model.get_new_doc(me.doctype);

				event[me.field_map.start] = me.get_system_datetime(selectionInfo.start);

				if (me.field_map.end) {
					event[me.field_map.end] = me.get_system_datetime(selectionInfo.end);
				}

				if (me.field_map.allDay) {
					event[me.field_map.allDay] = selectionInfo.allDay;

					if (selectionInfo.allDay) {
						const last_second = moment(selectionInfo.end).subtract(1, "seconds");
						event[me.field_map.end] = me.get_system_datetime(last_second.toDate());
					}
				}

				frappe.set_route("Form", me.doctype, event.name);
			},
			dateClick: function (info) {
				if (info.view.type === "dayGridMonth") {
					const $date_cell = $(
						"td[data-date=" + info.date.toISOString().slice(0, 10) + "]"
					);

					if ($date_cell.hasClass("date-clicked")) {
						me.fullCalendar.changeView("timeGridDay", info.date);
						me.$wrapper.find(".date-clicked").removeClass("date-clicked");

						// keep the view pill in sync with the programmatic view
						// change. Silent: double-click navigation is a jump, not a
						// choice of default view, so it isn't persisted.
						me.view_button_group.set_value("timeGridDay", { silent: true });
					}

					me.$wrapper.find(".date-clicked").removeClass("date-clicked");
					$date_cell.addClass("date-clicked");
				}
				return false;
			},
			buttonText: {
				today: __("Today"),
				month: __("Month"),
				week: __("Week"),
				day: __("Day"),
			},
			allDayContent: function () {
				return __("All Day");
			},
			eventDidMount: function (info) {
				$(info.el).tooltip({
					title: me.get_tooltip(info),
					placement: "auto",
					html: true,
				});
				me.addEventPopup(info);
			},
			eventWillUnmount: function (info) {
				$(info.el).tooltip("dispose");
				me.removeEventPopup(info);
			},
			slotMinTime: defaults.slots_start_time || "06:00:00",
			slotMaxTime: defaults.slots_end_time || "22:00:00",
			firstDay: defaults.first_day ?? frappe.datetime.get_first_day_of_the_week_index(),
		};

		if (this.options) {
			$.extend(this.cal_options, this.options);
		}
	}

	get_preview_fields() {
		this.preview_fields = this.list_view.meta.fields
			.filter((f) => f.in_list_view === 1 || f.in_preview === 1)
			.reduce((prev, curr) => {
				return Object.assign(prev, { [curr.fieldname]: curr.label });
			}, {});
	}

	get_tooltip(info) {
		const text = Object.keys(this.preview_fields)
			.filter((f) => f in info.event.extendedProps && info.event.extendedProps[f])
			.reduce((prev, f) => {
				return `${prev}<p><b>${__(this.preview_fields[f])}</b>: ${__(
					info.event.extendedProps[f] || ""
				)}</p>`;
			}, "");
		return text;
	}

	get_args(start, end) {
		return {
			doctype: this.doctype,
			start: this.get_system_datetime(start),
			end: this.get_system_datetime(end),
			filters: this.list_view.filter_area.get(),
			field_map: this.field_map,
		};
	}

	refresh() {
		this.fullCalendar.refetchEvents();
	}

	prepare_events(events) {
		const me = this;
		return (events || []).map((d) => {
			d.id = d.name;
			d.editable = frappe.model.can_write(d.doctype || me.doctype);
			d.classNames = d.classNames || [];

			// do not allow submitted/cancelled events to be moved / extended
			if (d.docstatus && d.docstatus > 0) {
				d.editable = false;
			}

			$.each(me.field_map, function (target, source) {
				d[target] = d[source];
			});

			if (!me.field_map.allDay && !me.no_all_day) {
				d.allDay = true;
			}

			// date-only events (Date fields, no time part) are all-day by
			// nature even when the config doesn't say so; without this the
			// exclusive-end rule below never runs and multi-day events
			// draw one day short (#25193)
			const date_only = (v) => v && !String(v).includes(" ");
			if (!d.allDay && date_only(d.start) && (!d.end || date_only(d.end))) {
				d.allDay = 1;
			}

			if (!me.field_map.convertToUserTz) d.convertToUserTz = 1;

			// convert to user tz
			if (d.convertToUserTz) {
				d.start = frappe.datetime.convert_to_user_tz(d.start);
				d.end = frappe.datetime.convert_to_user_tz(d.end);
			}

			// show event on single day if start or end date is invalid
			if (!frappe.datetime.validate(d.start) && d.end) {
				d.start = frappe.datetime.add_days(d.end, -1);
			}

			if (d.start && !frappe.datetime.validate(d.end)) {
				d.end = frappe.datetime.add_days(d.start, 1);
			}

			if ((d.secondary_status || d.status) && me.secondary_status_color) {
				d.classNames.push(me.secondary_status_color[d.secondary_status || d.status] || "");
			}

			// @dokos: the fork does the exclusive-end conversion through
			// fix_end_date_for_event_render (which round-trips via
			// get_system_datetime) instead of upstream's inline
			// `d.end = add_days(d.end, 1)` — this is the "exclusive-end rule"
			// the all-day inference above exists to reach.
			me.fix_end_date_for_event_render(d);

			// timed events get the accent bar (see calendar.scss)
			if (!d.allDay) {
				d.classNames = (d.classNames || []).concat("timed-event");
			}

			me.prepare_colors(d);

			d.title = frappe.utils.html2text(d.title);

			return d;
		});
	}

	prepare_colors(d) {
		// espresso color families that have a surface-X-1 / ink-X-7 pair
		const families = [
			"red",
			"green",
			"blue",
			"orange",
			"amber",
			"yellow",
			"gray",
			"purple",
			"pink",
			"cyan",
			"teal",
			"violet",
		];
		// the color picker's swatches — saturated hexes stored on existing
		// documents; render them as their espresso family so old colored
		// events pick up the soft look instead of a solid block
		const swatch_families = {
			"#449cf0": "blue",
			"#ecad4b": "amber",
			"#29cd42": "green",
			"#761acb": "purple",
			"#cb2929": "red",
			"#ed6396": "pink",
			"#4463f0": "violet",
			"#ec864b": "orange",
			"#4f9dd9": "cyan",
			"#39e4a5": "teal",
			"#b4cd29": "yellow",
		};

		const apply_family = (name) => {
			// "dark-green" was a color in the old palette
			if (name === "dark-green") name = "green";
			if (!families.includes(name)) name = "blue";
			// tokens instead of resolved hexes, so events flip in dark mode
			d.backgroundColor = `var(--surface-${name}-1)`;
			d.textColor = `var(--ink-${name}-7)`;
		};

		// known hexes (picker swatches, old standard palette) map to a
		// family; for hand-picked hexes, synthesize a family-style pair
		// from the hex itself — a barely-tinted surface and a text color
		// pulled toward ink. Mixing with tokens keeps them dark-mode aware.
		const apply_hex = (hex) => {
			hex = hex.toLowerCase();
			const family = swatch_families[hex] || frappe.ui.color.get_color_name(hex);
			if (family) {
				apply_family(family);
			} else {
				d.backgroundColor = `color-mix(in oklab, ${hex} 14%, var(--surface-base))`;
				d.textColor = `color-mix(in oklab, ${hex} 55%, var(--ink-gray-9))`;
			}
		};

		if (this.get_css_class) {
			let color_name = this.get_css_class(d);
			// color_map keeps the old semantic names (danger, success, ...) working
			color_name = this.color_map[color_name] || color_name || "blue";

			if (color_name.startsWith("#")) {
				if (frappe.ui.color.validate_hex(color_name)) {
					apply_hex(color_name);
					return d;
				}
				color_name = "blue";
			}
			apply_family(color_name);
		} else if (d.color && frappe.ui.color.validate_hex(d.color)) {
			apply_hex(d.color);
		} else {
			apply_family("blue");
		}

		return d;
	}

	update_event(info) {
		var me = this;
		let firstEvent = info.event;
		if (info.relatedEvents && info.relatedEvents[0]) {
			// let ok = false;
			// const dialog = new frappe.ui.Dialog();
			// me.refresh();
			const maybeFirst = info.relatedEvents[0];
			if (maybeFirst.start < firstEvent.start) {
				firstEvent = maybeFirst;
			}
		}
		frappe.model.remove_from_locals(
			firstEvent.extendedProps?.doctype || me.doctype,
			firstEvent.id
		);
		return frappe.call({
			method: me.update_event_method || "frappe.desk.calendar.update_event",
			args: me.get_update_args(firstEvent),
			callback: function (r) {
				if (r.exc) {
					frappe.show_alert(__("Unable to update event"));
					info.revert();
				} else {
					if (firstEvent.extendedProps?.rrule) {
						// Fetch some instances of the recurring event that might
						// be missing because they were out of the displayed range
						// but now should be displayed because the recurring event
						// was moved to an earlier date.
						me.refresh();
					}
				}
			},
			error: function () {
				info.revert();
			},
		});
	}

	get_update_args(event) {
		const me = this;
		let args = {
			name: event.id,
		};

		args[this.field_map.start] = me.get_system_datetime(event.start);

		if (this.field_map.allDay) args[this.field_map.allDay] = event.allDay;

		if (this.field_map.end) {
			if (!event.end) {
				event.end = event.start.add(1, "hour");
			}

			args[this.field_map.end] = me.get_system_datetime(event.end);

			if (args[this.field_map.allDay]) {
				args[this.field_map.end] = moment(event.end).subtract(1, "seconds");
			}
		}

		args.doctype = event.doctype || this.doctype;

		return { args: args, field_map: this.field_map };
	}

	fix_end_date_for_event_render(event) {
		if (event.allDay) {
			// We use inclusive end dates. This workaround fixes the rendering of events
			event.end = event.end
				? this.get_system_datetime(moment(event.end).add(1, "day"))
				: null;
		}
	}

	async addEventPopup(info) {
		if (!this.eventPopupManager) {
			this.eventPopupManager = new EventPopupManager({
				doctype: this.doctype,
				container: this.$wrapper.get(0),
			});
			await this.eventPopupManager.setup();
		}

		this.eventPopupManager.addEventPopup(info);
	}

	removeEventPopup(info) {
		this.eventPopupManager?.removeEventPopup(info);
	}

	set_min_max_times(events, opts = {}) {
		const minTimes = [];
		const maxTimes = [];
		for (const event of events) {
			if (!event.allDay) {
				const a = formatHms(event.start);
				const b = formatHms(event.end);
				if (a > b) {
					minTimes.push("00:00:00");
					maxTimes.push("24:00:00");
					break;
				}
				minTimes.push(a);
				maxTimes.push(b);
			}
		}

		if (opts.min) {
			minTimes.push(opts.min);
		}
		if (opts.max) {
			maxTimes.push(opts.max);
		}

		if (minTimes.length) {
			let minTime = minTimes.sort()[0];
			minTime = roundTime(minTime, Math.floor, -(opts.padHours || 0));
			this.fullCalendar.setOption("slotMinTime", minTime);
		}
		if (maxTimes.length) {
			let maxTime = maxTimes.sort()[maxTimes.length - 1];
			maxTime = roundTime(maxTime, Math.ceil, opts.padHours || 0);
			this.fullCalendar.setOption("slotMaxTime", maxTime);
		}

		function roundTime(time, func = Math.round, incr = 0) {
			let [hour, minute] = time.split(":").map(Number);
			minute = func(minute / 15) * 15;
			if (minute >= 60) {
				hour++;
				minute = 0;
			}
			hour = Math.min(Math.max(hour + incr, 0), 24);
			if (hour >= 24) {
				return "24:00:00";
			}
			hour = hour.toString().padStart(2, "0");
			minute = minute.toString().padStart(2, "0");
			return `${hour}:${minute}:00`;
		}

		function formatHms(str) {
			return new Date(str).toTimeString().slice(0, 8);
		}
	}
};
